<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import Modal from 'dashboard/components/Modal.vue';
import CannedResponseForm from './CannedResponseForm.vue';

const props = defineProps({
  responseContent: { type: String, default: '' },
  onClose: { type: Function, default: () => {} },
});

const { t } = useI18n();
const store = useStore();

const show = ref(true);
const isSaving = ref(false);
const shortCode = ref('');
const content = ref(props.responseContent);
const contentType = ref('markdown');
const selectedRoles = ref([]);
const attachmentIds = ref([]);

const customRoleOptions = useMapGetter('customRole/getCustomRoles');

const v$ = useVuelidate(
  {
    shortCode: { required, minLength: minLength(2) },
    content: { required },
  },
  { shortCode, content }
);

const shortCodeError = computed(() => v$.value.shortCode.$invalid);
const contentError = computed(() => v$.value.content.$invalid);

onMounted(() => {
  if (!customRoleOptions.value.length)
    store.dispatch('customRole/getCustomRole');
});

const addCannedResponse = async () => {
  isSaving.value = true;
  try {
    await store.dispatch('createCannedResponse', {
      short_code: shortCode.value,
      content: content.value,
      content_type: contentType.value,
      custom_role_ids: selectedRoles.value.map(role => role.id),
      blob_ids: attachmentIds.value,
    });
    useAlert(t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE'));
    props.onClose();
  } catch (error) {
    useAlert(error?.message || t('CANNED_MGMT.ADD.API.ERROR_MESSAGE'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="t('CANNED_MGMT.ADD.TITLE')"
        :header-content="t('CANNED_MGMT.ADD.DESC')"
      />
      <CannedResponseForm
        v-model:short-code="shortCode"
        v-model:content="content"
        v-model:content-type="contentType"
        v-model:selected-roles="selectedRoles"
        v-model:attachment-ids="attachmentIds"
        :short-code-error="shortCodeError"
        :content-error="contentError"
        :is-loading="isSaving"
        :custom-role-options="customRoleOptions"
        @short-code-blur="v$.shortCode.$touch"
        @content-blur="v$.content.$touch"
        @submit="addCannedResponse"
        @cancel="onClose"
      />
    </div>
  </Modal>
</template>
